import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from './layouts/PublicLayout';
import { getToolBySlug, toolRegistry } from './data/toolRegistry';
import { blogRegistry as articleEntries } from './data/blogRegistry';

/** Micro tools, for the index page's ItemList schema. */
const microToolEntries = toolRegistry.filter(tool =>
  tool.canonicalPath.startsWith('/micro-tools'),
);
import { CurrencyProvider } from './components/CurrencyProvider';
import { CurrencyToolbar } from './components/CurrencyToolbar';

// ── Lazy-loaded pages (code-splitting) ──────────────────────
const PublicHomepage = lazy(() => import('./pages/public/PublicHomepage'));
const ToolsIndex = lazy(() => import('./pages/public/ToolsIndex'));
const ResourcesIndex = lazy(() => import('./pages/public/ResourcesIndex'));
const ResourceArticlePage = lazy(() => import('./pages/public/ResourceArticlePage'));
const Contact = lazy(() => import('./pages/public/Contact'));
const FAQ = lazy(() => import('./pages/public/FAQ'));
const FAQArticlePage = lazy(() => import('./pages/public/FAQArticlePage'));
const MicroTools = lazy(() => import('./pages/public/MicroTools'));
const WebpToPngJpg = lazy(() => import('./pages/public/micro-tools/WebpToPngJpg'));
const PngToJpg = lazy(() => import('./pages/public/micro-tools/PngToJpg'));
const JpgToPng = lazy(() => import('./pages/public/micro-tools/JpgToPng'));
const ImageCompressor = lazy(() => import('./pages/public/micro-tools/ImageCompressor'));
const MergePdf = lazy(() => import('./pages/public/micro-tools/MergePdf'));
const SplitPdf = lazy(() => import('./pages/public/micro-tools/SplitPdf'));
const ProtectPdf = lazy(() => import('./pages/public/micro-tools/ProtectPdf'));
const JsonFormatter = lazy(() => import('./pages/public/micro-tools/JsonFormatter'));
const MarkdownToHtml = lazy(() => import('./pages/public/micro-tools/MarkdownToHtml'));
const WordCounter = lazy(() => import('./pages/public/micro-tools/WordCounter'));
const HourlyRateCalculator = lazy(() => import('./pages/public/micro-tools/HourlyRateCalculator'));
const LateFeeCalculator = lazy(() => import('./pages/public/micro-tools/LateFeeCalculator'));
const FaviconGenerator = lazy(() => import('./pages/public/micro-tools/FaviconGenerator'));
const SocialHeaderResizer = lazy(() => import('./pages/public/micro-tools/SocialHeaderResizer'));
const TdsCalculator = lazy(() => import('./pages/public/micro-tools/TdsCalculator'));
const FileHashChecker = lazy(() => import('./pages/public/micro-tools/FileHashChecker'));
const Base64Tool = lazy(() => import('./pages/public/micro-tools/Base64Tool'));
const FontChanger = lazy(() => import('./pages/public/micro-tools/FontChanger'));

const ESignTool = lazy(() => import('./pages/public/ESignTool'));
const PrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/public/TermsOfService'));
const NotFound = lazy(() => import('./pages/public/NotFound'));
import { SEOHead } from './components/seo/SEOHead';
import { ToolPageJsonLd, BreadcrumbJsonLd, ToolSchemaOnly, ItemListJsonLd, PageJsonLd } from './components/seo/JsonLd';
import { ToolSeoBlock } from './components/seo/ToolSeoBlock';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';

const GA_MEASUREMENT_ID = 'G-ETCRGVT9GH';

// App pages (heavy deps like jspdf, qrcode.react loaded only when needed)
const ScopeOfWorkGenerator = lazy(() => import('./pages/ScopeOfWorkGenerator'));
const LegalNoticeGenerator = lazy(() => import('./pages/LegalNoticeGenerator'));
const InvoiceGenerator = lazy(() => import('./pages/InvoiceGenerator'));
const DraftWatermarker = lazy(() => import('./pages/DraftWatermarker'));
const EscalationEmails = lazy(() => import('./pages/EscalationEmails'));
const NDAGenerator = lazy(() => import('./pages/NDAGenerator'));
const ProposalGenerator = lazy(() => import('./pages/ProposalGenerator'));
const InteractiveProposalBuilder = lazy(() => import('./pages/InteractiveProposalBuilder'));
const SignOffCertificate = lazy(() => import('./pages/SignOffCertificate'));
const TestimonialRelease = lazy(() => import('./pages/TestimonialRelease'));

// ── Loading Spinner ─────────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
        <p className="text-sm text-stone-500 tracking-wide">Loading…</p>
      </div>
    </div>
  );
}

// ── Scroll Reset On Navigation ─────────────────────────────
function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function GoogleAnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const gtag = (window as typeof window & {
      gtag?: (command: string, targetId: string, config?: Record<string, string>) => void;
    }).gtag;

    if (!gtag) return;

    gtag('config', GA_MEASUREMENT_ID, {
      page_path: `${location.pathname}${location.search}${location.hash}`,
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
}



// EditorLayout was removed with the /app/<tool> routes: tool pages now render
// inside PublicLayout, which supplies the header and mega-footer.

/**
 * A tool page: the working tool, with its reference content directly beneath.
 *
 * These render inside PublicLayout at the tool's canonical /tools/<slug> URL,
 * so a visitor arriving from search gets the tool immediately instead of an
 * interstitial article with a "use tool" button — and the page inherits the
 * public header and mega-footer, which is where most internal linking lives.
 * The old /app/<name> routes now 301 here.
 */
function ToolPage({
  children,
  toolSlug,
  showDocumentCurrencyToggle = true,
}: {
  children: React.ReactNode;
  toolSlug: string;
  showDocumentCurrencyToggle?: boolean;
}) {
  const tool = getToolBySlug(toolSlug);
  if (!tool) return null;

  return (
    <div className="bg-[#FAFAF9]">
      {/* The tool components carry no SEO of their own — the deleted landing
          page used to. Driving it from the registry here keeps titles and
          schema correct on client-side navigation as well as in the
          pre-rendered HTML. */}
      <SEOHead
        title={tool.title}
        description={tool.metaDescription}
        canonicalPath={tool.canonicalPath}
        ogImage={tool.ogImageFile}
        ogImageAlt={tool.h1}
      />
      <ToolPageJsonLd tool={tool} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', path: '/' },
          { name: tool.h1, path: tool.canonicalPath },
        ]}
      />
      <CurrencyToolbar showDocumentToggle={showDocumentCurrencyToggle} />
      {children}
      <ToolSeoBlock toolSlug={toolSlug} />
    </div>
  );
}

/**
 * slug → component, with the legacy /app path each tool used to live at.
 * Ordering here drives nothing; the registry remains the source of truth for
 * SEO, sitemap and navigation.
 */
const TOOL_ROUTES: {
  slug: string;
  legacyPath: string;
  element: React.ReactNode;
  showDocumentCurrencyToggle?: boolean;
}[] = [
  { slug: 'freelance-proposal-generator', legacyPath: '/app/proposal', element: <ProposalGenerator /> },
  { slug: 'interactive-html-proposal-builder', legacyPath: '/app/proposal-html', element: <InteractiveProposalBuilder /> },
  { slug: 'freelance-scope-of-work-generator', legacyPath: '/app/scope-of-work', element: <ScopeOfWorkGenerator /> },
  { slug: 'upi-invoice-generator-india', legacyPath: '/app/invoice', element: <InvoiceGenerator /> },
  { slug: 'freelance-nda-generator', legacyPath: '/app/nda', element: <NDAGenerator /> },
  { slug: 'draft-watermarker', legacyPath: '/app/watermark', element: <DraftWatermarker />, showDocumentCurrencyToggle: false },
  { slug: 'freelance-payment-escalation-emails', legacyPath: '/app/escalation', element: <EscalationEmails /> },
  { slug: 'freelance-legal-notice-generator-india', legacyPath: '/app/legal-notice', element: <LegalNoticeGenerator /> },
  { slug: 'freelance-sign-off-certificate-generator', legacyPath: '/app/signoff', element: <SignOffCertificate /> },
  { slug: 'freelance-ip-release-testimonial-form', legacyPath: '/app/testimonial', element: <TestimonialRelease /> },
];

// ── Root App ────────────────────────────────────────────────
interface AppProps {
  helmetContext?: Record<string, unknown>;
}

export default function App({ helmetContext }: AppProps) {
  return (
    <HelmetProvider context={helmetContext}>
      <CurrencyProvider>
        <Suspense fallback={<PageLoader />}>
          <ScrollToTopOnRouteChange />
          <GoogleAnalyticsRouteTracker />
          <GlobalErrorBoundary>
            <Routes>
              {/* ── Public Routes (SEO-optimized) ── */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<PublicHomepage />} />

                <Route path="/tools" element={<ToolsIndex />} />

                {/* Tool pages live at their canonical URL and are the tool. */}
                {TOOL_ROUTES.map(tool => (
                  <Route
                    key={tool.slug}
                    path={`/tools/${tool.slug}`}
                    element={
                      <ToolPage
                        toolSlug={tool.slug}
                        showDocumentCurrencyToggle={tool.showDocumentCurrencyToggle}
                      >
                        {tool.element}
                      </ToolPage>
                    }
                  />
                ))}
                <Route
                  path="/resources"
                  element={
                    <>
                      <ItemListJsonLd
                        name="Solo OS freelance guides"
                        path="/resources"
                        items={articleEntries.map(a => ({
                          name: a.title,
                          description: a.metaDescription,
                          path: a.canonicalPath,
                        }))}
                      />
                      <ResourcesIndex />
                    </>
                  }
                />
                <Route path="/resources/:slug" element={<ResourceArticlePage />} />
                <Route
                  path="/contact"
                  element={
                    <>
                      <PageJsonLd
                        type="ContactPage"
                        name="Contact Solo OS"
                        description="Get in touch with Solo OS for support, partnerships and product questions."
                        path="/contact"
                      />
                      <Contact />
                    </>
                  }
                />
                <Route
                  path="/micro-tools"
                  element={
                    <>
                      <ItemListJsonLd
                        name="Solo OS Micro Tools"
                        path="/micro-tools"
                        items={microToolEntries.map(tool => ({
                          name: tool.h1,
                          description: tool.subtitle,
                          path: tool.canonicalPath,
                        }))}
                      />
                      <MicroTools />
                    </>
                  }
                />
                <Route path="/micro-tools/webp-to-png-jpg" element={<><ToolSchemaOnly slug="webp-to-png-jpg" /><WebpToPngJpg /></>} />
                <Route path="/micro-tools/png-to-jpg" element={<><ToolSchemaOnly slug="png-to-jpg" /><PngToJpg /></>} />
                <Route path="/micro-tools/jpg-to-png" element={<><ToolSchemaOnly slug="jpg-to-png" /><JpgToPng /></>} />
                <Route path="/micro-tools/image-compressor" element={<><ToolSchemaOnly slug="image-compressor" /><ImageCompressor /></>} />
                <Route path="/micro-tools/merge-pdf" element={<><ToolSchemaOnly slug="merge-pdf" /><MergePdf /></>} />
                <Route path="/micro-tools/split-pdf" element={<><ToolSchemaOnly slug="split-pdf" /><SplitPdf /></>} />
                <Route path="/micro-tools/protect-pdf" element={<><ToolSchemaOnly slug="protect-pdf" /><ProtectPdf /></>} />
                <Route path="/micro-tools/json-formatter" element={<><ToolSchemaOnly slug="json-formatter" /><JsonFormatter /></>} />
                <Route path="/micro-tools/markdown-to-html" element={<><ToolSchemaOnly slug="markdown-to-html" /><MarkdownToHtml /></>} />
                <Route path="/micro-tools/word-character-counter" element={<><ToolSchemaOnly slug="word-character-counter" /><WordCounter /></>} />
                <Route path="/micro-tools/hourly-rate-calculator" element={<><ToolSchemaOnly slug="hourly-rate-calculator" /><HourlyRateCalculator /></>} />
                <Route path="/micro-tools/late-fee-calculator" element={<><ToolSchemaOnly slug="late-fee-calculator" /><LateFeeCalculator /></>} />
                <Route path="/micro-tools/favicon-generator" element={<><ToolSchemaOnly slug="favicon-generator" /><FaviconGenerator /></>} />
                <Route path="/micro-tools/social-header-resizer" element={<><ToolSchemaOnly slug="social-header-resizer" /><SocialHeaderResizer /></>} />
                <Route path="/micro-tools/tds-calculator" element={<><ToolSchemaOnly slug="tds-calculator" /><TdsCalculator /></>} />
                <Route path="/micro-tools/file-hash-checker" element={<><ToolSchemaOnly slug="file-hash-checker" /><FileHashChecker /></>} />
                <Route path="/micro-tools/base64-encoder-decoder" element={<><ToolSchemaOnly slug="base64-encoder-decoder" /><Base64Tool /></>} />
                <Route path="/micro-tools/font-changer" element={<><ToolSchemaOnly slug="font-changer" /><FontChanger /></>} />

                <Route path="/faq" element={<FAQ />} />
                <Route path="/faq/:slug" element={<FAQArticlePage />} />
                <Route path="/sign" element={<><ToolSchemaOnly slug="freelance-pdf-e-sign-tool" /><ESignTool /></>} />
                <Route
                  path="/legal/privacy-policy"
                  element={
                    <>
                      <PageJsonLd
                        name="Privacy Policy"
                        description="How Solo OS handles data. Documents are generated in your browser and never uploaded."
                        path="/legal/privacy-policy"
                      />
                      <PrivacyPolicy />
                    </>
                  }
                />
                <Route
                  path="/legal/terms-of-service"
                  element={
                    <>
                      <PageJsonLd
                        name="Terms of Service"
                        description="The terms that govern use of the free Solo OS document tools."
                        path="/legal/terms-of-service"
                      />
                      <TermsOfService />
                    </>
                  }
                />
              </Route>


              {/* ── Legacy tool URLs → canonical /tools/<slug> ── */}
              {/* The tool used to live at /app/<name> with a separate landing
                  page at /tools/<slug>. Both are now one page at the canonical
                  URL, so every old path redirects rather than duplicating. */}
              {TOOL_ROUTES.map(tool => (
                <Route
                  key={tool.legacyPath}
                  path={tool.legacyPath}
                  element={<Navigate to={`/tools/${tool.slug}`} replace />}
                />
              ))}
              {/* Even older bare paths, e.g. /invoice → /tools/upi-invoice-... */}
              {TOOL_ROUTES.map(tool => (
                <Route
                  key={`bare-${tool.slug}`}
                  path={tool.legacyPath.replace(/^\/app/, '')}
                  element={<Navigate to={`/tools/${tool.slug}`} replace />}
                />
              ))}

              {/* ── 404 Catch-All ── */}
              <Route element={<PublicLayout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </GlobalErrorBoundary>
          <Toaster position="bottom-right" richColors />
        </Suspense>
      </CurrencyProvider>
    </HelmetProvider>
  );
}
